import type { BusinessAssociate } from '@pn/core/domain/intel/business-associate';
import type { Company } from '@pn/core/domain/intel/company';
import { apiBAMapper } from '@pn/services/api/intel/apiBAMapper';
import { pnApiClient } from '@pn/services/api/pnApiClient';
import type { ApiBusinessAssociate, ApiNestedBusinessAssociate } from './types';

const requestBAsByCompanyId = async (companyId: Company['id']) => {
  const apiCompanyBAs = await pnApiClient.request<{
    business_associates: ApiBusinessAssociate[];
  }>({
    url: `v1/companies/${companyId}`,
  });

  return apiCompanyBAs.business_associates.map((apiBa) =>
    apiBAMapper.toDomainBusinessAssociate(apiBa)
  );
};

const requestAmalgamatedBAsByCompanyId = async (companyId: string) => {
  const apiCompanyBAs = await pnApiClient.request<ApiNestedBusinessAssociate[]>(
    {
      url: `v1/business_associates/fetch_amalgamations_by_company_brand_id/${companyId}`,
    }
  );

  return apiCompanyBAs.map((apiBa) =>
    apiBAMapper.toDomainAmalgamatedBusinessAssociate(apiBa)
  );
};

const requestBAsByNIds = async (nids: string[]) => {
  const apiCompanyBAs = await pnApiClient.request<ApiBusinessAssociate[]>({
    url: `v1/business_associates/fetch_by_nids`,
    method: 'POST',
    payload: {
      nid: nids,
    },
  });

  return apiCompanyBAs.map((apiBa) =>
    apiBAMapper.toDomainBusinessAssociate(apiBa)
  );
};

const requestBAsByProvinceAndSearchTerm = async (
  province: string,
  searchTerm: string
) => {
  const apiCompanyBAs = await pnApiClient.request<
    Record<string, ApiBusinessAssociate[]>
  >({
    url: `v1/business_associates/find_owner_by_province_and_name/${province}/${searchTerm}`,
  });

  const keys = Object.keys(apiCompanyBAs);
  const results: Record<string, BusinessAssociate[]> = {};
  keys.forEach((key) => {
    results[key] = apiCompanyBAs[key].map((apiBa) =>
      apiBAMapper.toDomainBusinessAssociate(apiBa)
    );
  });
  return results;
};

export const apiBAProvider = {
  getBAsForCompany: async (companyId: Company['id']) =>
    requestBAsByCompanyId(companyId),
  getAmalgamatedBAsByCompanyId: async (companyId: Company['id']) =>
    requestAmalgamatedBAsByCompanyId(companyId),
  getBAsByNIds: async (nids: string[]) => requestBAsByNIds(nids),
  getBAsByProvinceAndSearchTerm: async (province: string, searchTerm: string) =>
    requestBAsByProvinceAndSearchTerm(province, searchTerm),
};
